var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, FieldText, IconButton, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import moment from 'moment-timezone';
import { useCallback, useContext, useState } from 'react';
import { useSetState } from 'react-use';
import { flowsApi } from 'apis/flows';
import { analytics } from 'app/utils/segment';
import { AnnouncementPicker } from 'components/shared/announcementPicker';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { Dialog, } from 'components/shared/announcementPicker/announcementPicker';
import { TimezoneSelector } from 'components/timezoneSelector';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getExceptionsFromHolidays } from 'utils/holidays';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { OpeningHoursContext } from '../../openingHours.context';
import { HolidayCalendarSelector } from '../holidayCalendarSelector/holidayCalendarSelector';
import styles from './addModal.module.scss';
import { getDefaultRegularOpeningHours } from './utils/openingHours';
const nameField = 'name';
const timezoneField = 'timezone';
export const AddModal = ({ onClose }) => {
    const { schedules } = useContext(OpeningHoursContext);
    const isCreatingFirstSchedule = schedules.length === 0;
    const [{ audioMessage, countryCode, isClosed, name, timezone }, setState] = useSetState({
        audioMessage: null,
        countryCode: null,
        isClosed: false,
        name: '',
        timezone: '',
    });
    const [step, setStep] = useState(1);
    const [audioMessageDialog, setAudioMessageDialog] = useState(Dialog.Default);
    const [createSchedule, { isLoading: isFlowScheduleLoading }] = flowsApi.useCreateFlowScheduleMutation();
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const agentName = useAppSelector((state) => state.settings.profile.profile.name);
    const handleSubmit = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            analytics.businessHoursAddScheduleFormSubmitted(Object.assign({}, (countryCode && { countryCode, isClosed })));
            const holidaysResponse = countryCode
                ? yield dispatch(flowsApi.endpoints.readFlowsSchedulesHolidays.initiate({
                    country_code: countryCode,
                    timezone,
                }, {
                    forceRefetch: true,
                }))
                : null;
            const exceptions = holidaysResponse
                ? [
                    ...getExceptionsFromHolidays({
                        agentName,
                        holidays: (_a = holidaysResponse.data) === null || _a === void 0 ? void 0 : _a.data.holidays,
                        isClosed,
                    }),
                ]
                : [];
            if (audioMessage) {
                const scheduleResponse = yield createSchedule({
                    country_code: countryCode,
                    created_at: moment.utc().toISOString(),
                    name,
                    timezone,
                    announcement: audioMessage,
                    opening_hours: [
                        ...getDefaultRegularOpeningHours(agentName),
                        ...exceptions,
                    ],
                });
                if ('data' in scheduleResponse) {
                    notify({
                        title: 'Schedule created!',
                        type: 'success',
                        time: 5000,
                    }, NotificationType.Success);
                    const state = { isCreatingFirstSchedule };
                    navigate({
                        path: '/settings/businesshours/:id',
                        params: {
                            path: {
                                id: scheduleResponse.data.id,
                            },
                        },
                    }, { state });
                }
                else {
                    notify({
                        title: 'An error occurred when trying to create schedule.',
                    }, NotificationType.Error);
                }
            }
        }
        catch (error) {
            console.warn(error);
            notify({
                title: 'An error occurred when trying to create schedule.',
            }, NotificationType.Error);
        }
    }), [
        isCreatingFirstSchedule,
        agentName,
        audioMessage,
        countryCode,
        isClosed,
        navigate,
        name,
        timezone,
        createSchedule,
        dispatch,
        notify,
    ]);
    const handleTimezoneChange = useCallback((timezone) => {
        if (!timezone)
            return;
        setState({ timezone });
    }, [setState]);
    const handleNameChange = useCallback((event) => {
        if (!event)
            return;
        setState({ name: event.target.value });
    }, [setState]);
    const handleAudioMessageSelect = useCallback((audioMessage, type) => {
        analytics.businessHoursAddScheduleAudioMessageAdded({ type });
        setState({ audioMessage });
    }, [setState]);
    const handleCalendarChange = useCallback((countryCode, isClosed) => void setState({ countryCode, isClosed }), [setState]);
    const handleCancelClick = useCallback(() => {
        analytics.businessHoursAddScheduleFormCancelled();
        onClose();
    }, [onClose]);
    const isFirstStep = step === 1;
    return (_jsx(Modal, Object.assign({ isOpen: true, onClose: onClose }, { children: _jsx(Box, Object.assign({ width: "600px" }, { children: _jsxs("form", Object.assign({ onSubmit: handleSubmit }, { children: [_jsx(ModalHead, { className: styles.modalHead, title: _jsxs(_Fragment, { children: [!isFirstStep && (_jsx(IconButton, { icon: "angle-left", iconSize: 24, onClick: () => setStep(1), variant: "tertiary" })), isFirstStep ? 'Add new schedule' : 'Set audio message', _jsxs("span", Object.assign({ className: styles.steps }, { children: ["Step ", step, " of 2"] }))] }), onClose: onClose }), _jsx(ModalBody, { children: isFirstStep ? (_jsxs(_Fragment, { children: [_jsx(FieldText, { label: "Name", placeholder: "e.g. 'Italy' or 'HR Department'", required: true, input: {
                                        name: nameField,
                                        onChange: handleNameChange,
                                        value: name,
                                    } }), _jsx(TimezoneSelector, { placeholder: "Select the timezone for this schedule", required: true, input: {
                                        name: timezoneField,
                                        onChange: handleTimezoneChange,
                                        value: timezone,
                                    } }), _jsx(HolidayCalendarSelector, { clearable: true, onChange: handleCalendarChange })] })) : (_jsxs(_Fragment, { children: [audioMessageDialog == Dialog.Default && (_jsx(Text.Paragraph, Object.assign({ variant: "2", subdued: true, withMarginBottom: true }, { children: "Set up an audio message which your customers hear when they call you outside your business hours." }))), _jsx(AnnouncementPicker, { adapter: AdapterTypes.Announcement, fieldProps: {
                                        label: 'Standard audio message (outside business hours)',
                                    }, onDialogChange: setAudioMessageDialog, onSelect: handleAudioMessageSelect, selectedAudio: audioMessage })] })) }), _jsx(ModalFoot, { cancelOnClick: handleCancelClick, confirmProps: {
                            isLoading: isFlowScheduleLoading,
                            disabled: isFirstStep ? !(name && timezone) : !audioMessage,
                        }, confirmName: isFirstStep ? 'Next' : 'Create schedule', confirmOnClick: isFirstStep ? () => setStep(2) : handleSubmit })] })) })) })));
};
